import {
  bool,
  elementType,
  func,
  node,
  number,
  object,
  string,
} from "prop-types";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import cloneDeep from "lodash/cloneDeep";

import FnrInput2 from "UI/FnrInput2";
import FnrSlides from "UI/FnrSlides";

import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";
import stopEvents from "Utilities/stopEvents";

import ChildrenWithButtons from "./components/ChildrenWithButtons";
import InputWithButtons from "./components/InputWithButtons";
import { isEditing as isEditingKey, isReadOnly } from "./constants";
import { useActiveKey } from "./stateHooks";

const FnrPencilEditInputContext = createContext();

/** @returns {FnrPencilInputContext} */
const useFnrPencilEditInputContext = () =>
  useContext(FnrPencilEditInputContext);

const config = [
  {
    key: isReadOnly,
    value: ChildrenWithButtons,
  },
  { key: isEditingKey, value: InputWithButtons },
];

const FnrPencilEditInputContents = () => {
  const activeKey = useActiveKey();

  const { containerRef, onSave, labelSuffix, inputProps, isSaving } =
    useFnrPencilEditInputContext();

  return (
    <form onSubmit={onSave} ref={containerRef}>
      <FnrSlides
        clip={false}
        components={config}
        activeKey={activeKey}
        aria-label={`input-${labelSuffix}`}
        isLoading={isSaving}
        {...inputProps}
      />
    </form>
  );
};

const FnrPencilEditInput = forwardRef(
  /**
   *
   * @param {object} param0
   * @param {function} param0.onSave - the thing that should happen when the save button is clicked.
   * @param {string} param0.passOnSaveTo - the extended onSave function will be passed to the prop of the components. Omit if using built in save.
   * @param {import("react").ReactNode} param0.moreMenu a node to appear in the more menu
   * @param {import("react").ReactNode} param0.moreMenuPopperRef the ref passed to the moreMenu popper if it exists
   * @param {boolean} param0.moreMenuHasPopper set to false to display the moreMenu directly (no popper). defaults to true.
   * @param {boolean} param0.showSave - do you want the built in save button to display?
   * @param {boolean} param0.editOnInit - should the UI initialize in edit mode?
   * @param {import("react").ElementType} param0.Input - the component that should be used to edit the value. Defaults to FnrInput2.
   * @param {object} param0.inputProps - props passed to Input (whatever you passed in; FnrInput2 by default).
   * @param {boolean} param0.disabled - This allows you to dynamically lock out the edit functionaity
   * @param {string} param0.labelSuffix - This is appeneded to the aria lable of the toggle switch and other components
   * @param {boolean} param0.alwaysShowPencil - By default, the pencil only will display onHover
   * @param {FnrIconButtonProps} param0.iconButtonProps - props passed down to all of the iconButtons.
   * @param {function} param0.onEdit - If passed in, will fire with edit button click
   * @param {function} param0.onCancel - If passed in, will fire after with cancel button click. If you also want this to fire onSave...put it in the onSave function! 😘
   * @param {boolean} param0.countAsHoverFocused - overrides internal focused state when true
   * @param {number} param0.editButtonXAdjust
   * @returns
   */
  (
    {
      onSave: onSaveArg,
      passOnSaveTo,
      moreMenu,
      moreMenuHasPopper = true,
      moreMenuPopperRef,
      showSave,
      editOnInit,
      Input = FnrInput2,
      inputProps: inputPropsArg,
      children,
      disabled,
      labelSuffix,
      alwaysShowPencil,
      iconButtonProps,
      onEdit,
      onCancel,
      editButtonXAdjust,
      countAsHoverFocused,
    },
    passedInRef
  ) => {
    const localRef = useRef(null);
    const containerRef = passedInRef || localRef;
    const editButtonRef = useRef();
    const cancelButtonRef = useRef();
    const [isEditing, setIsEditing] = useState(editOnInit);
    const [isSaving, setIsSaving] = useState(false);
    const [moreMenuOpen, setMoreMenuOpen] = useState(false);

    const containerIsHoverFocused =
      useIsHoverFocused(containerRef, editButtonRef, cancelButtonRef) ||
      countAsHoverFocused ||
      moreMenuOpen;

    const onSave = useCallback(
      async (e) => {
        stopEvents(e);
        setIsSaving(true);
        await onSaveArg();
        if (containerIsHoverFocused && containerIsHoverFocused.onblur) {
          containerIsHoverFocused.onblur();
        }
        setIsEditing(false);
        setTimeout(() => setIsSaving(false), 300); // wait until animation finishes so the word "save" doesn't flash.
      },
      [containerIsHoverFocused, onSaveArg]
    );

    const inputProps = useMemo(() => {
      const extendedInputProps = cloneDeep(inputPropsArg) || {};
      if (passOnSaveTo) {
        extendedInputProps[passOnSaveTo] = onSave;
      }
      if (extendedInputProps.fullWidth) {
        delete extendedInputProps.fullWidth;
        extendedInputProps.fullWidthForComponent = true;
      }
      return extendedInputProps;
    }, [inputPropsArg, onSave, passOnSaveTo]);

    return (
      <FnrPencilEditInputContext.Provider
        value={{
          onSave,
          showSave,
          isSaving,
          moreMenu,
          moreMenuHasPopper,
          moreMenuPopperRef,
          isEditing,
          setIsEditing,
          children,
          Input,
          inputProps,
          containerRef,
          editButtonRef,
          cancelButtonRef,
          containerIsHoverFocused,
          disabled,
          labelSuffix,
          alwaysShowPencil,
          iconButtonProps,
          onEdit,
          onCancel,
          editButtonXAdjust,
          moreMenuOpen,
          setMoreMenuOpen,
        }}
      >
        <FnrPencilEditInputContents />
      </FnrPencilEditInputContext.Provider>
    );
  }
);

FnrPencilEditInput.propTypes = {
  onSave: func.isRequired,
  passOnSaveTo: string,
  moreMenu: object,
  moreMenuHasPopper: bool,
  moreMenuPopperRef: object,
  showSave: bool,
  editOnInit: bool,
  Input: elementType,
  children: node,
  disabled: bool,
  labelSuffix: string.isRequired,
  alwaysShowPencil: bool,
  iconButtonProps: object,
  inputProps: object,
  onEdit: func,
  onCancel: func,
  editButtonXAdjust: number,
  countAsHoverFocused: bool,
};

export { useFnrPencilEditInputContext };
export default FnrPencilEditInput;
